import { Page, Modal, TextField, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  DataTable,
  FormLayout,
  Checkbox,
  Banner,
  Tag,
  Collapsible,
  Divider,
} from '@shopify/polaris';
import { DeleteIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon } from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface ShippingProfile {
  id: string;
  name: string;
  shopify_rate_id?: string;
  lead_time_days: number;
  available_days: string[];
  cutoff_time: string;
  is_active: boolean;
}

interface ShippingZone {
  id: string;
  name: string;
  countries: string[];
  zoning_method: 'postcode' | 'country' | 'all';
  zip_code_mode: 'include' | 'exclude';
  postcode_patterns: string[];
  shipping_profiles: ShippingProfile[];
  is_active: boolean;
}

interface DeliverySurcharge {
  id: string;
  name: string;
  amount: number;
  applies_to: 'specific_dates' | 'day_of_week' | 'zone';
  conditions: Record<string, unknown>;
}

interface ShopifyShippingRate {
  id: string;
  name: string;
  price: string;
}

const DAYS_OF_WEEK = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

const COUNTRIES = [
  'Malta', 'United Kingdom', 'Ireland', 'United States', 'Canada', 'Australia',
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium',
];

export default function ShippingZones() {
  const { fetchApi, error } = useApi();
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [saveError, setSaveError] = useState('');
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [surcharges, setSurcharges] = useState<DeliverySurcharge[]>([]);
  const [shopifyRates, setShopifyRates] = useState<ShopifyShippingRate[]>([]);
  const [combineCalendars, setCombineCalendars] = useState(false);
  const [smartShippingRates, setSmartShippingRates] = useState(true);
  const [expandedZones, setExpandedZones] = useState<Set<string>>(new Set());
  const [expandedProfiles, setExpandedProfiles] = useState<Set<string>>(new Set());

  // Surcharge Modal
  const [surchargeModalActive, setSurchargeModalActive] = useState(false);
  const [surchargeName, setSurchargeName] = useState('');
  const [surchargeAmount, setSurchargeAmount] = useState('');
  const [surchargeType, setSurchargeType] = useState('specific_dates');

  // Country search
  const [countrySearch, setCountrySearch] = useState('');

  // Postcode pattern input
  const [newPostcodePattern, setNewPostcodePattern] = useState('');

  // Profile Modal
  const [profileModalActive, setProfileModalActive] = useState(false);
  const [profileZoneId, setProfileZoneId] = useState<string | null>(null);
  const [profileName, setProfileName] = useState('');
  const [profileLeadTime, setProfileLeadTime] = useState('1');
  const [profileCutoff, setProfileCutoff] = useState('14:00');
  const [profileDays, setProfileDays] = useState<string[]>(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const data = await fetchApi<{
      zones: ShippingZone[];
      surcharges: DeliverySurcharge[];
      shopifyRates: ShopifyShippingRate[];
      settings: { combineCalendars: boolean; smartShippingRates: boolean }
    }>('/shipping/zones');

    if (data) {
      setZones(data.zones || []);
      setSurcharges(data.surcharges || []);
      setShopifyRates(data.shopifyRates || []);
      setCombineCalendars(data.settings?.combineCalendars || false);
      setSmartShippingRates(data.settings?.smartShippingRates ?? true);
    }
  }

  const toggleZoneExpanded = (zoneId: string) => {
    setExpandedZones(prev => {
      const next = new Set(prev);
      if (next.has(zoneId)) {
        next.delete(zoneId);
      } else {
        next.add(zoneId);
      }
      return next;
    });
  };

  const toggleProfileExpanded = (profileId: string) => {
    setExpandedProfiles(prev => {
      const next = new Set(prev);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  };

  const addNewZone = () => {
    const newZone: ShippingZone = {
      id: `new-zone-${crypto.randomUUID()}`,
      name: `Zone ${zones.length + 1}`,
      countries: [],
      zoning_method: 'postcode',
      zip_code_mode: 'include',
      postcode_patterns: [],
      shipping_profiles: [],
      is_active: true,
    };
    setZones([...zones, newZone]);
    setExpandedZones(prev => new Set(prev).add(newZone.id));
  };

  const updateZone = (zoneId: string, updates: Partial<ShippingZone>) => {
    setZones(zones.map(z => z.id === zoneId ? { ...z, ...updates } : z));
  };

  const deleteZone = async (zoneId: string) => {
    if (confirm('Are you sure you want to delete this shipping zone?')) {
      if (zoneId.startsWith('new-') || await fetchApi(`/shipping/zones/${zoneId}`, { method: 'DELETE' })) setZones(current => current.filter(z => z.id !== zoneId));
    }
  };

  const addCountryToZone = (zoneId: string, country: string) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone && !zone.countries.includes(country)) {
      updateZone(zoneId, { countries: [...zone.countries, country] });
    }
    setCountrySearch('');
  };

  const removeCountryFromZone = (zoneId: string, country: string) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      updateZone(zoneId, { countries: zone.countries.filter(c => c !== country) });
    }
  };

  const addPostcodePattern = (zoneId: string) => {
    if (!newPostcodePattern.trim()) return;
    const zone = zones.find(z => z.id === zoneId);
    if (zone && !zone.postcode_patterns.includes(newPostcodePattern.trim())) {
      updateZone(zoneId, { postcode_patterns: [...zone.postcode_patterns, newPostcodePattern.trim()] });
    }
    setNewPostcodePattern('');
  };

  const removePostcodePattern = (zoneId: string, pattern: string) => {
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      updateZone(zoneId, { postcode_patterns: zone.postcode_patterns.filter(p => p !== pattern) });
    }
  };

  const clearAllPostcodes = (zoneId: string) => {
    updateZone(zoneId, { postcode_patterns: [] });
  };

  // Shipping profile functions
  const openAddProfileModal = (zoneId: string) => {
    setProfileZoneId(zoneId);
    setProfileName('');
    setProfileLeadTime('1');
    setProfileCutoff('14:00');
    setProfileDays(['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
    setProfileModalActive(true);
  };

  const saveProfile = () => {
    if (!profileZoneId) return;

    const profile: ShippingProfile = {
      id: `profile-${Date.now()}`,
      name: profileName,
      lead_time_days: parseInt(profileLeadTime, 10),
      cutoff_time: profileCutoff,
      available_days: profileDays,
      is_active: true,
    };

    const zone = zones.find(z => z.id === profileZoneId);
    if (zone) {
      updateZone(profileZoneId, {
        shipping_profiles: [...zone.shipping_profiles, profile]
      });
    }

    setProfileModalActive(false);
  };

  const deleteProfile = (zoneId: string, profileId: string) => {
    if (confirm('Are you sure you want to remove this shipping profile?')) {
      const zone = zones.find(z => z.id === zoneId);
      if (zone) {
        updateZone(zoneId, {
          shipping_profiles: zone.shipping_profiles.filter(p => p.id !== profileId)
        });
      }
    }
  };

  const toggleProfileDay = (day: string) => {
    setProfileDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleSaveSurcharge = () => {
    const amount = Number(surchargeAmount);
    if (!surchargeName.trim() || !surchargeAmount.trim() || !Number.isFinite(amount) || amount < 0) { setSaveError('Enter a surcharge name and a non-negative amount.'); return; }
    setSurcharges(current => [...current, { id: `new-surcharge-${crypto.randomUUID()}`, name: surchargeName.trim(), amount, applies_to: surchargeType as DeliverySurcharge['applies_to'], conditions: {} }]);
    setSurchargeModalActive(false); setSurchargeName(''); setSurchargeAmount(''); setNotice('Surcharge added to this draft. Save delivery surcharges to keep it.');
  };

  const saveZone = async (zone: ShippingZone) => {
    if (!zone.name.trim()) { setSaveError('Enter a name for every zone.'); return false; }
    const isNew = zone.id.startsWith('new-');
    const result = await fetchApi<{ zone: { id: string } }>(isNew ? '/shipping/zones' : `/shipping/zones/${zone.id}`, { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(zone) });
    if (!result) return false;
    if (isNew) {
      setZones(current => current.map(row => row.id === zone.id ? { ...row, id: result.zone.id } : row));
      setExpandedZones(current => { const next = new Set(current); if (next.delete(zone.id)) next.add(result.zone.id); return next; });
    }
    return true;
  };

  const saveSection = async (section: 'zones' | 'surcharges' | 'rates' | 'all') => {
    setSaving(true); setNotice(''); setSaveError('');
    try {
      if (section === 'zones' || section === 'all') {
        for (const zone of zones) if (!await saveZone(zone)) return;
        if (!await fetchApi('/shipping/settings', { method: 'PUT', body: JSON.stringify({ combineCalendars }) })) return;
      }
      if (section === 'surcharges' || section === 'all') {
        for (const surcharge of surcharges.filter(row => row.id.startsWith('new-'))) {
          const result = await fetchApi<{ surcharge: { id: string } }>('/shipping/surcharges', { method: 'POST', body: JSON.stringify({ name: surcharge.name, amount: surcharge.amount, applies_to: surcharge.applies_to, conditions: surcharge.conditions }) });
          if (!result) return;
          setSurcharges(current => current.map(row => row.id === surcharge.id ? { ...row, id: result.surcharge.id } : row));
        }
      }
      if (section === 'rates' || section === 'all') {
        if (!await fetchApi('/shipping/settings', { method: 'PUT', body: JSON.stringify({ smartShippingRates }) })) return;
      }
      setNotice(section === 'all' ? 'All shipping settings saved.' : `${{ zones: 'Shipping zones', surcharges: 'Delivery surcharges', rates: 'Shipping rate settings' }[section]} saved.`);
    } finally { setSaving(false); }
  };

  const removeSurcharge = async (surcharge: DeliverySurcharge) => {
    if (!confirm(`Remove ${surcharge.name}?`)) return;
    if (surcharge.id.startsWith('new-') || await fetchApi(`/shipping/surcharges/${surcharge.id}`, { method: 'DELETE' })) setSurcharges(current => current.filter(row => row.id !== surcharge.id));
  };

  const filteredCountries = COUNTRIES.filter(c =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const renderZoneCard = (zone: ShippingZone) => {
    const isExpanded = expandedZones.has(zone.id);

    return (
      <div key={zone.id} style={{ cursor: isExpanded ? undefined : 'pointer' }} onClick={event => {
        if (!isExpanded && !(event.target as HTMLElement).closest('button, a, input, select, textarea')) toggleZoneExpanded(zone.id);
      }}><Card>
        <BlockStack gap="400">
          {/* Zone Header */}
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
            <div style={{ flex: 1 }}><Button fullWidth textAlign="left" variant="plain" icon={isExpanded ? ChevronUpIcon : ChevronDownIcon} onClick={() => toggleZoneExpanded(zone.id)} accessibilityLabel={`${isExpanded ? 'Collapse' : 'Expand'} ${zone.name}`} ariaExpanded={isExpanded} ariaControls={`zone-${zone.id}`}>{zone.name}</Button></div>
            <Button icon={DeleteIcon} tone="critical" variant="plain" disabled={saving} onClick={() => deleteZone(zone.id)} accessibilityLabel={`Delete ${zone.name}`} />
          </div>

          {/* Zone Content (Collapsible) */}
          <Collapsible open={isExpanded} id={`zone-${zone.id}`}>
            <BlockStack gap="400">
              {/* Zone Name */}
              <TextField
                label="Zone Name"
                requiredIndicator value={zone.name}
                onChange={(value) => updateZone(zone.id, { name: value })}
                autoComplete="off"
              />

              {/* Countries */}
              <BlockStack gap="200">
                <Text as="span" variant="bodyMd">Search countries</Text>
                <TextField
                  label="Search countries"
                  labelHidden
                  value={countrySearch}
                  onChange={setCountrySearch}
                  placeholder="Search countries"
                  autoComplete="off"
                  connectedRight={
                    countrySearch && filteredCountries.length > 0 ? (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e1e3e5',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 100,
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}>
                        {filteredCountries.slice(0, 5).map(country => (
                          <div
                            key={country}
                            style={{ padding: '8px 12px', cursor: 'pointer' }}
                            onClick={() => addCountryToZone(zone.id, country)}
                          >
                            {country}
                          </div>
                        ))}
                      </div>
                    ) : undefined
                  }
                />
                {zone.countries.length > 0 && (
                  <InlineStack gap="200" wrap>
                    {zone.countries.map(country => (
                      <Tag key={country} onRemove={() => removeCountryFromZone(zone.id, country)}>
                        {country}
                      </Tag>
                    ))}
                  </InlineStack>
                )}
              </BlockStack>

              {/* Zoning Method */}
              <Select
                label="Zoning method"
                options={[
                  { label: 'Postcode based', value: 'postcode' },
                  { label: 'Country based', value: 'country' },
                  { label: 'All locations', value: 'all' },
                ]}
                value={zone.zoning_method}
                onChange={(value) => updateZone(zone.id, { zoning_method: value as ShippingZone['zoning_method'] })}
              />

              {/* Postcode settings (only if postcode-based) */}
              {zone.zoning_method === 'postcode' && (
                <>
                  <Select
                    label="ZIP code mode"
                    options={[
                      { label: 'Include Specific Postcodes', value: 'include' },
                      { label: 'Exclude Specific Postcodes', value: 'exclude' },
                    ]}
                    value={zone.zip_code_mode}
                    onChange={(value) => updateZone(zone.id, { zip_code_mode: value as 'include' | 'exclude' })}
                  />

                  <BlockStack gap="200">
                    <Text as="span" variant="bodyMd">Postcode pattern</Text>
                    <InlineStack gap="200">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Postcode pattern"
                          labelHidden
                          value={newPostcodePattern}
                          onChange={setNewPostcodePattern}
                          placeholder="e.g., BKR* or BKR 40* (prefix with *)"
                          autoComplete="off"
                        />
                      </div>
                      <Button icon={PlusIcon} onClick={() => addPostcodePattern(zone.id)}>Add pattern</Button>
                      <Button variant="plain" tone="critical" onClick={() => clearAllPostcodes(zone.id)}>
                        Clear all
                      </Button>
                    </InlineStack>
                    {zone.postcode_patterns.length > 0 && (
                      <InlineStack gap="200" wrap>
                        {zone.postcode_patterns.map(pattern => (
                          <Tag key={pattern} onRemove={() => removePostcodePattern(zone.id, pattern)}>
                            {pattern}
                          </Tag>
                        ))}
                      </InlineStack>
                    )}
                  </BlockStack>
                </>
              )}

              <Divider />

              {/* Shipping Profiles */}
              <BlockStack gap="300">
                <Text as="h4" variant="headingSm">Shipping Profiles</Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Configure which Shopify shipping rates are available and on which days
                </Text>

                {zone.shipping_profiles.map(profile => {
                  const isProfileExpanded = expandedProfiles.has(profile.id);
                  return (
                    <Card key={profile.id} padding="300">
                      <BlockStack gap="300">
                        <InlineStack align="space-between">
                          <InlineStack gap="200" blockAlign="center">
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              {profile.name}
                            </Text>
                          </InlineStack>
                          <InlineStack gap="100">
                            <Button
                              icon={DeleteIcon}
                              tone="critical"
                              variant="plain"
                              onClick={() => deleteProfile(zone.id, profile.id)}
                              accessibilityLabel="Delete profile"
                            />
                            <Button
                              icon={isProfileExpanded ? ChevronUpIcon : ChevronDownIcon}
                              variant="plain"
                              onClick={() => toggleProfileExpanded(profile.id)}
                              accessibilityLabel={isProfileExpanded ? 'Collapse' : 'Expand'}
                            />
                          </InlineStack>
                        </InlineStack>

                        <Collapsible open={isProfileExpanded} id={`profile-${profile.id}`}>
                          <BlockStack gap="300">
                            <InlineStack gap="400">
                              <div style={{ flex: 1 }}>
                                <TextField
                                  label="Lead time (days)"
                                  type="number"
                                  value={String(profile.lead_time_days)}
                                  onChange={(value) => {
                                    const updated = { ...profile, lead_time_days: parseInt(value, 10) || 0 };
                                    updateZone(zone.id, {
                                      shipping_profiles: zone.shipping_profiles.map(p =>
                                        p.id === profile.id ? updated : p
                                      )
                                    });
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <TextField
                                  label="Cutoff time"
                                  type="time"
                                  value={profile.cutoff_time}
                                  onChange={(value) => {
                                    const updated = { ...profile, cutoff_time: value };
                                    updateZone(zone.id, {
                                      shipping_profiles: zone.shipping_profiles.map(p =>
                                        p.id === profile.id ? updated : p
                                      )
                                    });
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            </InlineStack>

                            <BlockStack gap="200">
                              <Text as="span" variant="bodySm">Available days</Text>
                              <InlineStack gap="200" wrap>
                                {DAYS_OF_WEEK.map(day => (
                                  <Button
                                    key={day.value}
                                    pressed={profile.available_days.includes(day.value)}
                                    onClick={() => {
                                      const newDays = profile.available_days.includes(day.value)
                                        ? profile.available_days.filter(d => d !== day.value)
                                        : [...profile.available_days, day.value];
                                      const updated = { ...profile, available_days: newDays };
                                      updateZone(zone.id, {
                                        shipping_profiles: zone.shipping_profiles.map(p =>
                                          p.id === profile.id ? updated : p
                                        )
                                      });
                                    }}
                                    size="slim"
                                  >
                                    {day.label.slice(0, 3)}
                                  </Button>
                                ))}
                              </InlineStack>
                            </BlockStack>
                          </BlockStack>
                        </Collapsible>
                      </BlockStack>
                    </Card>
                  );
                })}

                <Button
                  fullWidth
                  variant="plain"
                  icon={PlusIcon}
                  onClick={() => openAddProfileModal(zone.id)}
                >
                  Add shipping profile
                </Button>
              </BlockStack>
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card></div>
    );
  };

  return (
    <Page
      title="Shipping Settings"
      subtitle="Control the operational aspects of your delivery system"
      primaryActionPosition="footer"
      primaryAction={{
        content: 'Save Changes',
        onAction: () => void saveSection('all'),
        loading: saving,
      }}
    >
      <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}><Layout>
        {notice && <Layout.Section><Banner tone="success">{notice}</Banner></Layout.Section>}
        {saveError && <Layout.Section><Banner tone="critical">{saveError}</Banner></Layout.Section>}
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Shipping Zones */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Dates by customer location (Shipping zones)</Text>
                <Text as="p" tone="subdued">Configure delivery schedules for different customer locations</Text>
              </BlockStack>

              <BlockStack gap="300">
                {zones.map(zone => renderZoneCard(zone))}

                <Button fullWidth variant="plain" icon={PlusIcon} onClick={addNewZone}>
                  Add shipping zone
                </Button>
              </BlockStack>

              <Checkbox
                label="Combine all shipping profiles into one calendar"
                checked={combineCalendars}
                onChange={setCombineCalendars}
                helpText="When enabled, customers will see all available dates in one calendar. Dates with surcharges will display the surcharge amount."
              />
              <InlineStack><Button onClick={() => void saveSection('zones')} loading={saving}>Save shipping zones</Button></InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Delivery Surcharges */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Delivery surcharges (cart page)</Text>
                <Text as="p" tone="subdued">Configure delivery surcharges for specific dates or shipping profiles</Text>
              </BlockStack>

              {surcharges.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'text', 'text']}
                  headings={['Name', 'Amount', 'Applies To', 'Actions']}
                  rows={surcharges.map(s => [
                    s.name,
                    `£${s.amount.toFixed(2)}`,
                    s.applies_to.replace('_', ' '),
                    <Button key={s.id} size="slim" tone="critical" onClick={() => void removeSurcharge(s)}>Remove</Button>,
                  ])}
                />
              ) : null}

              <Button fullWidth variant="plain" icon={PlusIcon} onClick={() => setSurchargeModalActive(true)}>
                Add delivery surcharge
              </Button>
              <InlineStack><Button onClick={() => void saveSection('surcharges')} loading={saving}>Save delivery surcharges</Button></InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Smart Shipping Rate Display */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">Smart shipping rate display</Text>
                <Text as="p" tone="subdued">Only show shipping rates valid for the selected delivery date</Text>
              </BlockStack>

              <Checkbox
                label="Smart shipping rate display"
                checked={smartShippingRates}
                onChange={setSmartShippingRates}
                helpText="When enabled, Whenly will only show shipping rates that are valid for the selected delivery date."
              />
              <InlineStack><Button onClick={() => void saveSection('rates')} loading={saving}>Save shipping rate settings</Button></InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout></fieldset>

      {/* Add/Edit Profile Modal */}
      <Modal
        open={profileModalActive}
        onClose={() => setProfileModalActive(false)}
        title="Add Shipping Profile"
        primaryAction={{
          icon: PlusIcon, content: 'Save',
          onAction: saveProfile,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setProfileModalActive(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Select
              label="Shopify Shipping Rate"
              options={[
                { label: 'Custom name', value: '' },
                ...shopifyRates.map(rate => ({ label: rate.name, value: rate.id }))
              ]}
              value=""
              onChange={(value) => {
                const rate = shopifyRates.find(r => r.id === value);
                if (rate) setProfileName(rate.name);
              }}
              helpText="Select from your Shopify shipping rates or enter a custom name"
            />

            <TextField
              label="Profile Name"
              requiredIndicator value={profileName}
              onChange={setProfileName}
              placeholder="e.g., Standard Delivery, Saturday"
              autoComplete="off"
            />

            <TextField
              label="Lead Time (days)"
              type="number"
              value={profileLeadTime}
              onChange={setProfileLeadTime}
              helpText="Minimum days before delivery available"
              autoComplete="off"
            />

            <TextField
              label="Cutoff Time"
              type="time"
              value={profileCutoff}
              onChange={setProfileCutoff}
              helpText="Orders after this time push to next available day"
              autoComplete="off"
            />

            <BlockStack gap="200">
              <Text as="span" variant="bodyMd">Available Days</Text>
              <InlineStack gap="200" wrap>
                {DAYS_OF_WEEK.map(day => (
                  <Button
                    key={day.value}
                    pressed={profileDays.includes(day.value)}
                    onClick={() => toggleProfileDay(day.value)}
                    size="slim"
                  >
                    {day.label.slice(0, 3)}
                  </Button>
                ))}
              </InlineStack>
            </BlockStack>
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Surcharge Modal */}
      <Modal
        open={surchargeModalActive}
        onClose={() => setSurchargeModalActive(false)}
        title="Add Delivery Surcharge"
        primaryAction={{
          icon: PlusIcon, content: 'Add',
          onAction: handleSaveSurcharge,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setSurchargeModalActive(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Surcharge Name"
              requiredIndicator value={surchargeName}
              onChange={setSurchargeName}
              placeholder="e.g., Weekend Delivery, Same Day"
              autoComplete="off"
            />

            <TextField
              label="Amount"
              type="number"
              requiredIndicator value={surchargeAmount}
              onChange={setSurchargeAmount}
              prefix="£"
              autoComplete="off"
            />

            <Select
              label="Applies To"
              options={[
                { label: 'Specific Dates', value: 'specific_dates' },
                { label: 'Day of Week', value: 'day_of_week' },
                { label: 'Shipping Zone', value: 'zone' },
              ]}
              value={surchargeType}
              onChange={setSurchargeType}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
